import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import copy from "../../public/locales/en/analytics.json";
import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchAdminUsers,
  deleteAdminUser,
} from "../store/slices/adminUsersSlice";

import type { UserActivitySegment } from "@lingoshade/domain";
import type { ReactElement } from "react";

const labels = copy.users;

export default function UsersPage(): ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { users, loading, error, pagination } = useAppSelector(
    (state) => state.adminUsers,
  );
  const search = params.get("search") ?? "";
  const source = params.get("source") ?? "";
  const rawSegment = params.get("segment") ?? "all";
  const segment: UserActivitySegment = Object.keys(labels.segments).includes(
    rawSegment,
  )
    ? (rawSegment as UserActivitySegment)
    : "all";
  const pageValue = Number(params.get("page") ?? 1);
  const page =
    Number.isInteger(pageValue) && pageValue > 0 && pageValue <= 10000
      ? pageValue
      : 1;
  const limit = 50;

  const changeFilter = (key: string, value: string): void => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );
  };
  useEffect(() => {
    let request: { abort: () => void } | undefined;
    const timer = window.setTimeout((): void => {
      request = dispatch(
        fetchAdminUsers({
          page,
          limit,
          search: search || undefined,
          segment,
          source: source || undefined,
        }),
      );
    }, 250);
    return (): void => {
      window.clearTimeout(timer);
      request?.abort();
    };
  }, [dispatch, page, search, segment, source]);

  const handleDelete = async (userId: string): Promise<void> => {
    if (!window.confirm(labels.confirmDelete)) return;
    const result = await dispatch(deleteAdminUser(userId));
    if (deleteAdminUser.fulfilled.match(result)) {
      const nextPage = users.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) changeFilter("page", String(nextPage));
      else
        void dispatch(
          fetchAdminUsers({
            page,
            limit,
            search: search || undefined,
            segment,
            source: source || undefined,
          }),
        );
    }
  };
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {labels.title}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", my: 3 }}>
        <TextField
          label={labels.search}
          value={search}
          onChange={(event): void => changeFilter("search", event.target.value)}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />
        <TextField
          select
          label={labels.activity}
          value={segment}
          onChange={(event): void =>
            changeFilter("segment", event.target.value)
          }
          sx={{ minWidth: 260 }}
        >
          {(Object.keys(labels.segments) as UserActivitySegment[]).map(
            (key) => (
              <MenuItem key={key} value={key}>
                {labels.segments[key]}
              </MenuItem>
            ),
          )}
        </TextField>
        <TextField
          label={labels.source}
          value={source}
          onChange={(event): void => changeFilter("source", event.target.value)}
          slotProps={{ htmlInput: { maxLength: 253 } }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {copy.audienceNote}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper>
        <TableContainer>
          <Table aria-label={labels.title}>
            <TableHead>
              <TableRow>
                {[
                  labels.email,
                  labels.subscription,
                  labels.lastPractice,
                  labels.practiceDays,
                  labels.origin,
                  labels.created,
                  labels.actions,
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {labels.empty}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflowWrap: "anywhere",
                          color:
                            user.role === "ADMIN"
                              ? "primary.main"
                              : "text.primary",
                        }}
                      >
                        {user.email}
                      </Typography>
                      {(user.firstName || user.lastName) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {[user.firstName, user.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.subscription?.status ?? labels.none}
                        size="small"
                        color={user.subscription ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {user.role !== "USER" || user.isAnonymous
                        ? "—"
                        : user.activity?.lastPracticeAt
                          ? new Date(
                              user.activity.lastPracticeAt,
                            ).toLocaleString("en", { timeZone: "UTC" })
                          : labels.never}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {user.activity
                        ? `${user.activity.activeDays7} / ${user.activity.activeDays30}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {user.registrationUtmSource ??
                        user.registrationReferrerHost ??
                        copy.campaign.unknown}
                      {user.registrationUtmCampaign && (
                        <Typography variant="caption" display="block">
                          {user.registrationUtmCampaign}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(
                        user.registeredAt ?? user.createdAt,
                      ).toLocaleDateString("en", { timeZone: "UTC" })}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label={labels.details}
                        onClick={(): void => {
                          void navigate(`/users/${user.id}`);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={labels.delete}
                        onClick={(): void => {
                          void handleDelete(user.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination?.totalItems ?? 0}
          page={page - 1}
          rowsPerPage={limit}
          rowsPerPageOptions={[limit]}
          onPageChange={(_event, next): void =>
            changeFilter("page", String(next + 1))
          }
        />
      </Paper>
    </Box>
  );
}
